import {
  ListItem,
  Title,
  Icon,
  ChildrenContainer,
  ItemChildren,
  ListContainer,
  NestedGroupContainer,
  NestedDisclosure,
  NestedChildren,
  NestedContractContainer
} from "./styled";
import useCheckWebAuthFn from "../../../hooks/use-check-web-auth-fn";
import useLeftMenuList from "../../../hooks/use-left-menu-list";
import { useLocation } from "react-router-dom";
import { Icon as IconifyIcon } from "@iconify/react";
import PlusIcon from "@iconify/icons-mdi/plus-box";
import MinusIcon from "@iconify/icons-mdi/minus-box";
import styled from "styled-components";
import { useCallback, useEffect, useState } from "react";
import { SettingsNestedGroup } from "../../work-type-shared/settings-nested-group";
import {
  getCurrentAncestorIds,
  getVisibleChildren,
  hasCurrentDescendant,
  isMenuGroup,
  isMenuNodeVisible
} from "./menu-tree";

/*
- 작동방식 -
menuStatus와 workingAuthWeb값에 따라 메뉴가 보이고 안보이고 결정이 되어야 한다.
webMenuAuthList에서 비트연산 후 useLeftMenuList함수를 통해 
괸리자 메뉴중 webMenuAuthList의 값으로 새롭게 활성화될 메뉴를 만든다.
그후 menuStatus로 한번더 메뉴를 필터링해서 렌더링해준다.
*/

const IconContainer = styled.div`
  color: ${props => props.theme.colors.darkGrey};
  :hover {
    color: white;
  }
  :active {
    transform: scale(0.94);
  }
`;

const LeftMenuList = props => {
  const { handleList, loginIdInfo, open, menuStatus, workingAuthWeb } = props;
  const { pathname } = useLocation();
  const { webMenuAuthList } = useCheckWebAuthFn({
    workingAuthWeb: workingAuthWeb?.working_auth_web,
    webEtcFlag: menuStatus?.web_etc_flag
  });
  const { newMenu: listData } = useLeftMenuList({
    webMenuAuthList
  });
  const [expandedMenuIds, setExpandedMenuIds] = useState(
    () => new Set(getCurrentAncestorIds(listData, pathname, loginIdInfo))
  );

  useEffect(() => {
    const ancestorIds = getCurrentAncestorIds(listData, pathname, loginIdInfo);
    setExpandedMenuIds(current => new Set([...current, ...ancestorIds]));
  }, [listData, loginIdInfo, pathname]);

  const toggleMenu = useCallback(id => {
    setExpandedMenuIds(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const renderNestedNode = (node, depth) => {
    if (!isMenuNodeVisible(node, loginIdInfo)) {
      return null;
    }
    if (!isMenuGroup(node)) {
      const current = node.pathname === pathname;
      return (
        <ItemChildren
          as="button"
          type="button"
          key={node.id}
          selected={current}
          aria-current={current ? "page" : undefined}
          onClick={handleList(node.pathname)}
        >
          <Title>{node.title}</Title>
        </ItemChildren>
      );
    }

    const children = getVisibleChildren(node, loginIdInfo);
    const current = hasCurrentDescendant(node, pathname, loginIdInfo);
    if (children.every(child => !isMenuGroup(child))) {
      return (
        <NestedContractContainer key={node.id} $depth={depth}>
          <SettingsNestedGroup
            label={node.title}
            items={children.map(child => ({
              id: child.id,
              label: child.title,
              path: child.pathname,
              onSelect: path => handleList(path)()
            }))}
            currentPath={pathname}
            defaultExpanded={current}
          />
        </NestedContractContainer>
      );
    }

    const expanded = expandedMenuIds.has(node.id);
    return (
      <NestedGroupContainer key={node.id}>
        <NestedDisclosure
          type="button"
          aria-label={node.title}
          aria-expanded={expanded}
          aria-current={current ? "location" : undefined}
          selected={current}
          $depth={depth}
          onClick={() => toggleMenu(node.id)}
        >
          <span>{node.title}</span>
          <span aria-hidden="true">{expanded ? "−" : "+"}</span>
        </NestedDisclosure>
        {expanded && (
          <NestedChildren>
            {children.map(child => renderNestedNode(child, depth + 1))}
          </NestedChildren>
        )}
      </NestedGroupContainer>
    );
  };

  const renderComponent = useCallback(
    item => {
      const current = hasCurrentDescendant(item, pathname, loginIdInfo);
      const expanded = isMenuGroup(item) && expandedMenuIds.has(item.id);
      return (
      <ListContainer key={item.id} selected={current} open={open}>
        <ListItem
          type="button"
          open={open}
          selected={current}
          aria-label={item.title}
          aria-current={
            current ? (isMenuGroup(item) ? "location" : "page") : undefined
          }
          aria-expanded={isMenuGroup(item) ? expanded : undefined}
          onClick={() =>
            isMenuGroup(item)
              ? toggleMenu(item.id)
              : handleList(item.pathname)()
          }
          title={item.title}
        >
          <Icon
            icon={item.icon}
            selected={current}
            size="lg"
          />
          {open && (
            <Title>{item.title}</Title>
          )}
          {open && isMenuGroup(item) && (
            <IconContainer aria-hidden="true">
              {expanded ? (
                <IconifyIcon
                  icon={MinusIcon}
                  selected={current}
                  style={{ fontSize: "30px" }}
                />
              ) : (
                <IconifyIcon
                  icon={PlusIcon}
                  selected={current}
                  style={{ fontSize: "30px" }}
                />
              )}
            </IconContainer>
          )}
        </ListItem>
        {open && isMenuGroup(item) && expanded && (
          <ChildrenContainer open isOpen>
            {getVisibleChildren(item, loginIdInfo).map(child =>
              renderNestedNode(child, 1)
            )}
          </ChildrenContainer>
        )}
      </ListContainer>
      );
    },
    [expandedMenuIds, handleList, loginIdInfo, open, pathname, toggleMenu]
  );

  const checkStatus = useCallback(
    item => {
      if (!isMenuNodeVisible(item, loginIdInfo)) {
        return null;
      }
      if (item.key && menuStatus[item.key] === 1) {
        return renderComponent(item);
      } else if (!item.key) {
        return renderComponent(item);
      } else {
        // status 값이 2는 미사용이며, 다른 값은 통계 메뉴가 보여야함
        const isStatisticsMenu = item.key === "web_report_flag"; // 통계 메뉴인지 확인
        const isStatisticsMenuActive = menuStatus[item.key] !== 2; // 2는 미사용
        if (isStatisticsMenu && isStatisticsMenuActive) {
          return renderComponent(item);
        }
        return null;
      }
    },
    [loginIdInfo, menuStatus, renderComponent]
  );

  return <>{menuStatus && listData.map(checkStatus)}</>;
};

export default LeftMenuList;
